import React, { createContext, useContext, useReducer, useEffect } from 'react';
import {
  login,
  registerUser,
  getUserProfile,
  logout as logoutAPI,
  createCompanyWithOwner,
  updateUserProfile,
  completeSocialSignup,
  AUTH_SESSION_EXPIRED_EVENT,
  emitSessionDataInvalidation,
} from '../services/api';
import { clearPromptCrewTransientState } from '../utils/promptCrewTransientState';

/** Cognito access token for GlobalSignOut — not the Id token used as API Bearer. */
const COGNITO_ACCESS_TOKEN_KEY = 'cognitoAccessToken';

// Initial state
const initialState = {
  user: null,
  token: localStorage.getItem('token'),
  isAuthenticated: false,
  isLoading: true,
  error: null,
};

// Action types
const AUTH_ACTIONS = {
  LOGIN_START: 'LOGIN_START',
  LOGIN_SUCCESS: 'LOGIN_SUCCESS',
  LOGIN_FAILURE: 'LOGIN_FAILURE',
  REGISTER_START: 'REGISTER_START',
  REGISTER_SUCCESS: 'REGISTER_SUCCESS',
  REGISTER_FAILURE: 'REGISTER_FAILURE',
  CREATE_COMPANY_START: 'CREATE_COMPANY_START',
  CREATE_COMPANY_SUCCESS: 'CREATE_COMPANY_SUCCESS',
  CREATE_COMPANY_FAILURE: 'CREATE_COMPANY_FAILURE',
  UPDATE_PROFILE_START: 'UPDATE_PROFILE_START',
  UPDATE_PROFILE_SUCCESS: 'UPDATE_PROFILE_SUCCESS',
  UPDATE_PROFILE_FAILURE: 'UPDATE_PROFILE_FAILURE',
  LOGOUT: 'LOGOUT',
  SET_LOADING: 'SET_LOADING',
  CLEAR_ERROR: 'CLEAR_ERROR',
  SET_USER: 'SET_USER',
};

// Reducer
const authReducer = (state, action) => {
  switch (action.type) {
    case AUTH_ACTIONS.LOGIN_START:
    case AUTH_ACTIONS.REGISTER_START:
    case AUTH_ACTIONS.CREATE_COMPANY_START:
      return {
        ...state,
        isLoading: true,
        error: null,
      };

    // Profile updates do not set global loading (avoids unmounting app in ProtectedRoute)
    case AUTH_ACTIONS.UPDATE_PROFILE_START:
      return {
        ...state,
        error: null,
      };
    
    case AUTH_ACTIONS.LOGIN_SUCCESS:
    case AUTH_ACTIONS.REGISTER_SUCCESS:
    case AUTH_ACTIONS.CREATE_COMPANY_SUCCESS:
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      };
    
    case AUTH_ACTIONS.UPDATE_PROFILE_SUCCESS:
      return {
        ...state,
        user: {
          ...state.user,
          ...action.payload.user,
          licenseAssignment:
            action.payload.user.licenseAssignment !== undefined && action.payload.user.licenseAssignment !== null
              ? action.payload.user.licenseAssignment
              : state.user?.licenseAssignment ?? null,
          viewedHelpers: {
            ...(state.user?.viewedHelpers || {}),
            ...(action.payload.user?.viewedHelpers || {}),
          },
        },
        isLoading: false,
        error: null,
      };
    
    case AUTH_ACTIONS.LOGIN_FAILURE:
    case AUTH_ACTIONS.REGISTER_FAILURE:
    case AUTH_ACTIONS.CREATE_COMPANY_FAILURE:
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload,
      };
    
    case AUTH_ACTIONS.UPDATE_PROFILE_FAILURE:
      return {
        ...state,
        isLoading: false,
        error: action.payload,
      };
    
    case AUTH_ACTIONS.LOGOUT:
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      };
    
    case AUTH_ACTIONS.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload,
      };
    
    case AUTH_ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        error: null,
      };
    
    case AUTH_ACTIONS.SET_USER:
      return {
        ...state,
        user: action.payload,
        token: localStorage.getItem('token'),
        isAuthenticated: true,
        isLoading: false,
      };
    
    default:
      return state;
  }
};

// Create context
const AuthContext = createContext();

// Auth provider component
export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Check if user is authenticated on app load
  // For auth pages, we don't need to wait - set loading to false immediately if no token
  useEffect(() => {
    const AUTH_CHECK_TIMEOUT_MS = 15000;

    const checkAuth = async (retryCount = 0) => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const timeoutPromise = new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Auth check timeout')), AUTH_CHECK_TIMEOUT_MS)
          );
          const response = await Promise.race([getUserProfile(), timeoutPromise]);
          dispatch({
            type: AUTH_ACTIONS.SET_USER,
            payload: response.profile,
          });
        } catch (error) {
          console.error('Auth check failed:', error);

          // Only logout if it's an authentication error (401, 403)
          // Don't logout for network errors or server issues
          const status = error?.response?.status ?? error?.status;
          const isTimeout = error?.message === 'Auth check timeout';
          if (status === 401 || status === 403) {
            localStorage.removeItem('token');
            localStorage.removeItem(COGNITO_ACCESS_TOKEN_KEY);
            clearPromptCrewTransientState();
            dispatch({ type: AUTH_ACTIONS.LOGOUT });
          } else if (!isTimeout && retryCount < 2) {
            // Retry for network errors (up to 2 retries)
            setTimeout(() => checkAuth(retryCount + 1), 1000 * (retryCount + 1)); // 1s, 2s delays
          } else {
            // After retries or timeout, show loading as false so the app can render
            dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
          }
        }
      } else {
        // No token - set loading to false immediately (don't block auth pages)
        dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
      }
    };

    // Check if we're on an auth page - if so, don't block rendering
    const isAuthPage = window.location.pathname.startsWith('/auth/');
    if (isAuthPage && !localStorage.getItem('token')) {
      // On auth page with no token - set loading false immediately
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
    } else {
      // Otherwise, run the full auth check
      checkAuth();
    }
  }, []);

  // Expired / invalid token on API calls: clear session and return to login (no Cognito API — may also 401)
  useEffect(() => {
    let redirecting = false;
    const onSessionExpired = () => {
      if (redirecting) return;
      redirecting = true;
      localStorage.removeItem('token');
      localStorage.removeItem(COGNITO_ACCESS_TOKEN_KEY);
      clearPromptCrewTransientState();
      dispatch({ type: AUTH_ACTIONS.LOGOUT });
      const pathname = window.location.pathname;
      const isPublicCapabilityPage =
        pathname.startsWith('/shared/') ||
        pathname.startsWith('/public/capabilities/') ||
        /^\/community\/[^/]+$/.test(pathname);
      if (isPublicCapabilityPage) {
        redirecting = false;
        return;
      }
      const base = `${window.location.origin}/auth/login`;
      window.location.replace(`${base}?sessionExpired=1`);
    };
    window.addEventListener(AUTH_SESSION_EXPIRED_EVENT, onSessionExpired);
    return () => window.removeEventListener(AUTH_SESSION_EXPIRED_EVENT, onSessionExpired);
  }, []);

  // Login function
  const loginUser = async (credentials) => {
    dispatch({ type: AUTH_ACTIONS.LOGIN_START });
    try {
      const response = await login(credentials);
      const { token, user, cognitoAccessToken } = response;

      clearPromptCrewTransientState();
      localStorage.setItem('token', token);
      if (cognitoAccessToken) {
        localStorage.setItem(COGNITO_ACCESS_TOKEN_KEY, cognitoAccessToken);
      }
      
      // Fetch full profile with companyName and teamName after login
      try {
        const profileResponse = await getUserProfile();
        const fullProfile = profileResponse.profile || profileResponse;
        
        dispatch({
          type: AUTH_ACTIONS.LOGIN_SUCCESS,
          payload: { user: fullProfile, token },
        });
      } catch (profileError) {
        // If profile fetch fails, use the basic user data from login
        console.warn('Failed to fetch full profile after login, using basic user data:', profileError);
        dispatch({
          type: AUTH_ACTIONS.LOGIN_SUCCESS,
          payload: { user, token },
        });
      }
      
      return response;
    } catch (error) {
      dispatch({
        type: AUTH_ACTIONS.LOGIN_FAILURE,
        payload: error.message || 'Login failed',
      });
      throw error;
    }
  };

  // Register function
  const registerUserAccount = async (userData) => {
    dispatch({ type: AUTH_ACTIONS.REGISTER_START });
    try {
      const response = await registerUser(userData);
      const { token, user, requiresVerification, cognitoAccessToken } = response;

      if (!token || requiresVerification) {
        dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
        return response;
      }

      clearPromptCrewTransientState();
      localStorage.setItem('token', token);
      if (cognitoAccessToken) {
        localStorage.setItem(COGNITO_ACCESS_TOKEN_KEY, cognitoAccessToken);
      }
      
      // Fetch full profile with companyName and teamName after registration
      try {
        const profileResponse = await getUserProfile();
        const fullProfile = profileResponse.profile || profileResponse;
        
        dispatch({
          type: AUTH_ACTIONS.REGISTER_SUCCESS,
          payload: { user: fullProfile, token },
        });
      } catch (profileError) {
        // If profile fetch fails, use the basic user data from registration
        console.warn('Failed to fetch full profile after registration, using basic user data:', profileError);
        dispatch({
          type: AUTH_ACTIONS.REGISTER_SUCCESS,
          payload: { user, token },
        });
      }
      
      return response;
    } catch (error) {
      dispatch({
        type: AUTH_ACTIONS.REGISTER_FAILURE,
        payload: error.message || 'Registration failed',
      });
      throw error;
    }
  };

  // Create company with owner function
  const createCompanyWithOwnerAccount = async (companyData) => {
    dispatch({ type: AUTH_ACTIONS.CREATE_COMPANY_START });
    try {
      const response = await createCompanyWithOwner(companyData);
      const { token, user, requiresVerification, cognitoAccessToken } = response;

      if (!token || requiresVerification) {
        dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
        return response;
      }

      clearPromptCrewTransientState();
      localStorage.setItem('token', token);
      if (cognitoAccessToken) {
        localStorage.setItem(COGNITO_ACCESS_TOKEN_KEY, cognitoAccessToken);
      }
      
      // Fetch full profile with companyName and teamName after company creation
      try {
        const profileResponse = await getUserProfile();
        const fullProfile = profileResponse.profile || profileResponse;
        
        dispatch({
          type: AUTH_ACTIONS.CREATE_COMPANY_SUCCESS,
          payload: { user: fullProfile, token },
        });
      } catch (profileError) {
        // If profile fetch fails, use the basic user data from company creation
        console.warn('Failed to fetch full profile after company creation, using basic user data:', profileError);
        dispatch({
          type: AUTH_ACTIONS.CREATE_COMPANY_SUCCESS,
          payload: { user, token },
        });
      }
      
      return response;
    } catch (error) {
      dispatch({
        type: AUTH_ACTIONS.CREATE_COMPANY_FAILURE,
        payload: error.message || 'Company creation failed',
      });
      throw error;
    }
  };

  const refreshUserProfile = async () => {
    const token = localStorage.getItem('token');
    if (!token) return undefined;
    try {
      const response = await getUserProfile();
      const profile = response.profile || response;
      dispatch({
        type: AUTH_ACTIONS.SET_USER,
        payload: profile,
      });
      emitSessionDataInvalidation('prompthub:invalidate-profile');
      return profile;
    } catch (error) {
      console.error('refreshUserProfile failed:', error);
      return undefined;
    }
  };

  // Update user profile function
  const updateUserProfileData = async (profileData) => {
    try {
      dispatch({ type: AUTH_ACTIONS.UPDATE_PROFILE_START });
      
      const response = await updateUserProfile(profileData);
      const { user } = response;
      
      dispatch({
        type: AUTH_ACTIONS.UPDATE_PROFILE_SUCCESS,
        payload: { user },
      });
      
      return response;
    } catch (error) {
      dispatch({
        type: AUTH_ACTIONS.UPDATE_PROFILE_FAILURE,
        payload: error.message || 'Profile update failed',
      });
      throw error;
    }
  };

  const setAuthenticatedUser = (user, token, cognitoAccessToken = null) => {
    clearPromptCrewTransientState();
    if (token) {
      localStorage.setItem('token', token);
    }
    if (cognitoAccessToken) {
      localStorage.setItem(COGNITO_ACCESS_TOKEN_KEY, cognitoAccessToken);
    }
    dispatch({
      type: AUTH_ACTIONS.SET_USER,
      payload: user,
    });
  };

  const completeSocialSignupAccount = async (profileData, token) => {
    dispatch({ type: AUTH_ACTIONS.REGISTER_START });
    try {
      const response = await completeSocialSignup(profileData, token);
      let user = response.user || response.profile || response;
      const socialAccess = sessionStorage.getItem('socialCognitoAccessToken');
      clearPromptCrewTransientState();
      localStorage.setItem('token', token);
      if (socialAccess) {
        localStorage.setItem(COGNITO_ACCESS_TOKEN_KEY, socialAccess);
        sessionStorage.removeItem('socialCognitoAccessToken');
      }
      sessionStorage.removeItem('socialAuthToken');
      try {
        const profileResponse = await getUserProfile();
        user = profileResponse.profile || profileResponse;
      } catch (profileErr) {
        console.warn('Profile refresh after social signup failed:', profileErr);
      }
      dispatch({
        type: AUTH_ACTIONS.REGISTER_SUCCESS,
        payload: { user, token },
      });
      return response;
    } catch (error) {
      dispatch({
        type: AUTH_ACTIONS.REGISTER_FAILURE,
        payload: error.message || 'Social signup failed',
      });
      throw error;
    }
  };

  // Logout function
  const logoutUser = async () => {
    try {
      const cognitoAccessToken = localStorage.getItem(COGNITO_ACCESS_TOKEN_KEY);
      if (cognitoAccessToken) {
        await logoutAPI(cognitoAccessToken);
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem(COGNITO_ACCESS_TOKEN_KEY);
      clearPromptCrewTransientState();
      dispatch({ type: AUTH_ACTIONS.LOGOUT });
    }
  };

  // Clear error function
  const clearError = () => {
    dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });
  };

  const value = {
    ...state,
    loginUser,
    registerUserAccount,
    createCompanyWithOwnerAccount,
    updateUserProfileData,
    refreshUserProfile,
    setAuthenticatedUser,
    completeSocialSignupAccount,
    logoutUser,
    clearError,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
